import { Loader2 } from "lucide-react";
import type { ToolPart } from "@/components/ui/ChatPanel";
import { DocumentListCard } from "./DocumentListCard";
import { DocumentCard } from "./DocumentCard";
import { AuditCard } from "./AuditCard";
import { AuditListCard } from "./AuditListCard";
import { ChecklistItemCard } from "./ChecklistItemCard";
import { ChecklistProgressCard } from "./ChecklistProgressCard";
import { ClauseCard } from "./ClauseCard";
import { CoverageCard } from "./CoverageCard";
import { ActivityTimelineCard } from "./ActivityTimelineCard";
import {
  isAuditListResult,
  isAuditDetailResult,
  isChecklistResult,
  isChecklistItemDetailResult,
  isClauseSearchResult,
  isDocChecklistLinksResult,
  isActivityResult,
  isSuggestedDocsResult,
} from "./chatCardUtils";

interface RichMessageRendererProps {
  toolParts: ToolPart[];
}

function isSearchResults(
  output: unknown,
): output is Array<Record<string, unknown>> {
  return Array.isArray(output) && output.length > 0;
}

function isDocumentResult(
  output: unknown,
): output is { id: string; fileName: string; summary?: string | null } {
  return (
    typeof output === "object" &&
    output !== null &&
    ("id" in output || "_id" in output) &&
    ("fileName" in output || "title" in output)
  );
}

function isCountResult(output: unknown): output is { count: number } {
  return typeof output === "object" && output !== null && "count" in output;
}

// Tool-specific loading labels
const TOOL_LOADING_LABELS: Record<string, string> = {
  listDocuments: "Loading documents...",
  searchDocuments: "Searching documents...",
  getDocumentDetails: "Loading document details...",
  countDocuments: "Counting documents...",
  listAudits: "Loading audits...",
  getAuditDetail: "Loading audit details...",
  listChecklistItems: "Checking checklist items...",
  getChecklistItemDetail: "Loading checklist item...",
  getChecklistSuggestedDocuments: "Finding suggested documents...",
  searchClauses: "Searching clauses...",
  getDocumentChecklistLinks: "Checking document coverage...",
  getRecentActivity: "Loading recent activity...",
};

const TOOL_ERROR_LABELS: Record<string, string> = {
  listDocuments: "Couldn't load documents",
  searchDocuments: "Document search failed",
  getDocumentDetails: "Couldn't load document",
  countDocuments: "Couldn't count documents",
  listAudits: "Couldn't load audits",
  getAuditDetail: "Couldn't load audit details",
  listChecklistItems: "Couldn't load checklist",
  getChecklistItemDetail: "Couldn't load checklist item",
  getChecklistSuggestedDocuments: "Couldn't find suggested documents",
  searchClauses: "Clause search failed",
  getDocumentChecklistLinks: "Couldn't load coverage data",
  getRecentActivity: "Couldn't load activity",
};

function ToolLoadingIndicator({ toolName }: { toolName: string }) {
  const label = TOOL_LOADING_LABELS[toolName] ?? "Working...";
  return (
    <div className="flex items-center gap-2 py-1.5 text-xs text-muted animate-in fade-in">
      <Loader2 size={12} className="animate-spin" />
      <span>{label}</span>
    </div>
  );
}

function ToolErrorIndicator({ toolName }: { toolName: string }) {
  const label = TOOL_ERROR_LABELS[toolName] ?? "Tool failed";
  return (
    <div className="flex items-center gap-2 py-1.5 text-xs text-destructive/70 animate-in fade-in">
      <span className="inline-block h-2 w-2 rounded-full bg-destructive/50 shrink-0" />
      <span>{label}</span>
    </div>
  );
}

export function RichMessageRenderer({ toolParts }: RichMessageRendererProps) {
  if (!toolParts.length) return null;

  return (
    <div className="space-y-2">
      {toolParts.map((part, i) => {
        const key = `${part.toolName}-${i}`;

        // Show error indicator when tool has failed
        if (part.state === "output-error") {
          return <ToolErrorIndicator key={key} toolName={part.toolName} />;
        }

        // Show loading indicator when tool is still running
        if (part.state !== "output-available") {
          return <ToolLoadingIndicator key={key} toolName={part.toolName} />;
        }

        // --- Document tools ---

        if (
          part.toolName === "listDocuments" &&
          Array.isArray(part.output) &&
          part.output.length > 0
        ) {
          return <DocumentListCard key={key} results={part.output} />;
        }

        if (
          part.toolName === "searchDocuments" &&
          isSearchResults(part.output)
        ) {
          return <DocumentListCard key={key} results={part.output} />;
        }

        if (
          part.toolName === "getDocumentDetails" &&
          isDocumentResult(part.output)
        ) {
          const doc = {
            id: String(
              (part.output as Record<string, unknown>)._id ??
                (part.output as Record<string, unknown>).id ??
                "",
            ),
            fileName: String(
              (part.output as Record<string, unknown>).fileName ??
                (part.output as Record<string, unknown>).title ??
                "Unknown",
            ),
            summary:
              ((part.output as Record<string, unknown>).summary as
                | string
                | null) ?? null,
            processingStatus:
              ((part.output as Record<string, unknown>).processingStatus as
                | string
                | null) ?? null,
            fileType:
              ((part.output as Record<string, unknown>).fileType as
                | string
                | null) ?? null,
          };
          return <DocumentCard key={key} document={doc} />;
        }

        if (part.toolName === "countDocuments" && isCountResult(part.output)) {
          return (
            <div
              key={key}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent/10 px-2.5 py-1 text-xs font-medium text-accent"
            >
              {part.output.count} document{part.output.count !== 1 ? "s" : ""}
            </div>
          );
        }

        // --- Audit tools ---

        if (part.toolName === "listAudits" && isAuditListResult(part.output)) {
          return <AuditListCard key={key} audits={part.output} />;
        }

        if (
          part.toolName === "getAuditDetail" &&
          isAuditDetailResult(part.output)
        ) {
          return (
            <AuditCard
              key={key}
              audit={part.output}
              variant="detail"
              detail={part.output}
            />
          );
        }

        // --- Checklist tools ---

        if (
          part.toolName === "listChecklistItems" &&
          isChecklistResult(part.output)
        ) {
          return <ChecklistProgressCard key={key} data={part.output} />;
        }

        if (
          part.toolName === "getChecklistItemDetail" &&
          isChecklistItemDetailResult(part.output)
        ) {
          return (
            <ChecklistItemCard key={key} item={part.output} variant="detail" />
          );
        }

        if (
          part.toolName === "getChecklistSuggestedDocuments" &&
          isSuggestedDocsResult(part.output)
        ) {
          const docs = part.output.suggestedDocuments;
          if (!docs.length) return null;
          return (
            <DocumentListCard
              key={key}
              results={docs.map((d) => ({
                _id: d.documentId,
                fileName: d.fileName,
                summary: d.reason,
              }))}
            />
          );
        }

        // --- Compliance tools ---

        if (
          part.toolName === "searchClauses" &&
          isClauseSearchResult(part.output)
        ) {
          return <ClauseCard key={key} data={part.output} />;
        }

        if (
          part.toolName === "getDocumentChecklistLinks" &&
          isDocChecklistLinksResult(part.output)
        ) {
          return <CoverageCard key={key} data={part.output} />;
        }

        // --- Activity ---

        if (
          part.toolName === "getRecentActivity" &&
          isActivityResult(part.output)
        ) {
          return <ActivityTimelineCard key={key} events={part.output} />;
        }

        return null;
      })}
    </div>
  );
}
